// src/pages/Auth/Login.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Divider,
  IconButton,
  InputAdornment,
  FormControlLabel,
  Checkbox,
  Link,
  CircularProgress,
} from '@mui/material';
import {
  Business,
  Visibility,
  VisibilityOff,
  Email,
  Lock,
  Login as LoginIcon,
  PersonAdd,
  Security,
  TrendingUp,
  AccountBalance,
  Shield,
  Speed,
  Support,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

// Services
import AuthService from '../../services/authService';


// Enhanced validation schema
const schema = yup.object({
  email: yup
    .string()
    .required('Email address is required')
    .email('Please enter a valid email address')
    .matches(
      /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
      'Invalid email format'
    ),
  password: yup
    .string()
    .required('Password is required')
    .min(8, 'Password must be at least 8 characters')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
      'Password must contain at least one uppercase letter, one lowercase letter, and one number'
    ),
});

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [rememberMe, setRememberMe] = useState(() => {
    // Check if user previously selected rememberMe
    const savedRememberMe = localStorage.getItem('rememberMe');
    return savedRememberMe === 'true';
  });
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [isBlocked, setIsBlocked] = useState(false);
  const [blockTimer, setBlockTimer] = useState(0);

  // Success message shown after registration or password reset process.
  const successMessage = location.state?.message;

  const { control, handleSubmit, formState: { errors, isValid }, watch } = useForm({
    resolver: yupResolver(schema),
    mode: 'onChange',
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const watchedEmail = watch('email');
  const watchedPassword = watch('password');

  // Block timer effect
  useEffect(() => {
    let interval;
    if (isBlocked && blockTimer > 0) {
      interval = setInterval(() => {
        setBlockTimer(prev => {
          if (prev <= 1) {
            setIsBlocked(false);
            setLoginAttempts(0);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isBlocked, blockTimer]);

  const onSubmit = async (data) => {
    if (isBlocked) {
      setLoginError(`Too many failed attempts. Please wait ${blockTimer} seconds.`);
      return;
    }

    setLoading(true);
    setLoginError('');

    try {
      // AuthService kullanarak login - rememberMe'yi de gönder
      const result = await AuthService.login({
        ...data,
        rememberMe: rememberMe
      });

      if (result.success) {
        // Success - store token and user data
        AuthService.setAuthData(
          result.data.token,
          result.data.user,
          rememberMe,
          result.data.refreshToken
        );

        // Reset attempts
        setLoginAttempts(0);
        
        // Dispatch custom event to update user information
        window.dispatchEvent(new CustomEvent('userLogin'));
        
        // Redirect to dashboard
        navigate('/dashboard', { replace: true });
      } else {
        // Handle failed login
        const newAttempts = loginAttempts + 1;
        setLoginAttempts(newAttempts);

        if (newAttempts >= 3) {
          setIsBlocked(true);
          setBlockTimer(300); // 5 minute block
          setLoginError('Too many failed attempts. You will not be able to log in for 5 minutes.');
        } else {
          setLoginError(`${result.message} You have ${3 - newAttempts} attempts remaining.`);
        }
      }
    } catch (error) {
      setLoginError('Server error. Please try again later.');
      console.error('Login error:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const features = [
    {
      title: 'Banking Level Security',
      description: 'SSL encryption and multi-factor authentication',
      icon: <Shield />,
    },
    {
      title: 'Fast Transaction Process',
      description: 'Credit approval and transaction completion in minutes',
      icon: <Speed />,
    },
    {
      title: '24/7 Support',
      description: 'Uninterrupted customer service and technical support',
      icon: <Support />,
    },
  ];

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
      display: 'flex',
      alignItems: 'center',
      py: 4
    }}>
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          
          {/* Left Side - Branding & Features */}
          <Box sx={{ flex: 1, display: { xs: 'none', md: 'block' }, color: 'white' }}>
            {/* Logo & Branding */}
            <Box sx={{ mb: 6 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Business sx={{ fontSize: 56, mr: 2 }} />
                <Box>
                  <Typography variant="h3" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                    DPG Finance
                  </Typography>
                  <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 300 }}>
                    Digital Payment
                  </Typography>
                </Box>
              </Box>
              
              <Typography variant="h5" sx={{ mb: 2, fontWeight: 500 }}>
                For Your Corporate Finance Solutions
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.8, lineHeight: 1.6 }}>
                Easily manage your financial transactions with our secure, fast and user-friendly platform. 
                Credit requests, guarantee transactions and limit management all in one platform.
              </Typography>
            </Box>
            {/* Features */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {features.map((feature, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Box sx={{ 
                    p: 1.5, 
                    borderRadius: 2, 
                    backgroundColor: 'rgba(255,255,255,0.15)',
                    color: 'white',
                    flexShrink: 0
                  }}>
                    {feature.icon}
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8, lineHeight: 1.5 }}>
                      {feature.description}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Right Side - Login Form */}
          <Box sx={{ flex: 1, maxWidth: 500 }}>
            {/* Paper - for card view */}
            <Paper elevation={24} sx={{ p: 5, borderRadius: 4 }}>
              {/* Header */}
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <LoginIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                  Sign In
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Securely sign in to your DPG Finance account
                </Typography>
              </Box>

              {/* Success Message */}
              {successMessage && (
                <Alert severity="success" sx={{ mb: 3 }}>
                  {successMessage}
                </Alert>
              )}

              {/* Login Error */}
              {loginError && (
                <Alert 
                  severity={isBlocked ? "warning" : "error"} 
                  sx={{ mb: 3 }}
                  icon={isBlocked ? <Security /> : undefined}
                >
                  {isBlocked ? (
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Security Lock Active
                      </Typography>
                      <Typography variant="body2">
                        Time remaining: {formatTime(blockTimer)}
                      </Typography>
                    </Box>
                  ) : (
                    loginError
                  )}
                </Alert>
              )}

              {/* Login Form */}
              <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ mb: 4 }}>
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Corporate Email Address"
                      type="email"
                      error={!!errors.email}
                      helperText={errors.email?.message}
                      sx={{ mb: 3 }}
                      disabled={isBlocked}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Email color={errors.email ? 'error' : watchedEmail ? 'primary' : 'disabled'} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />

                <Controller
                  name="password"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Password"
                      type={showPassword ? 'text' : 'password'}
                      error={!!errors.password}
                      helperText={errors.password?.message}
                      sx={{ mb: 3 }}
                      disabled={isBlocked}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Lock color={errors.password ? 'error' : watchedPassword ? 'primary' : 'disabled'} />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                              disabled={isBlocked}
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />

                {/* Remember Me & Forgot Password */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <FormControlLabel
                    control={
                      <Checkbox 
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                        disabled={isBlocked}
                        sx={{
                          color: 'primary.main',
                          '&.Mui-checked': {
                            color: 'primary.main',
                          },
                        }}
                      />
                    }
                    label="Remember Me"
                  />
                  <Link 
                    component="button"
                    type="button"
                    variant="body2" 
                    onClick={() => navigate('/forgot-password')}
                    sx={{ 
                      textDecoration: 'none', 
                      cursor: 'pointer',
                      '&:hover': { textDecoration: 'underline' }
                    }}
                    disabled={isBlocked}
                  >
                    Forgot Password
                  </Link>
                </Box>

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading || isBlocked}
                  sx={{ 
                    mb: 2, 
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                    boxShadow: '0 4px 15px rgba(25, 118, 210, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                      boxShadow: '0 6px 20px rgba(25, 118, 210, 0.4)',
                      transform: 'translateY(-1px)',
                    },
                    '&:disabled': {
                      background: 'rgba(0, 0, 0, 0.12)',
                      color: 'rgba(0, 0, 0, 0.26)',
                      boxShadow: 'none',
                      transform: 'none',
                    }
                  }}
                >
                  {loading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={20} color="inherit" />
                      Signing in...
                    </Box>
                  ) : isBlocked ? (
                    `Blocked (${formatTime(blockTimer)})`
                  ) : (
                    'Sign In'
                  )}
                </Button>

                {/* Login Attempts Warning */}
                {loginAttempts > 0 && !isBlocked && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      {loginAttempts} failed attempts. You have {3 - loginAttempts} attempts remaining.
                    </Typography>
                  </Alert>
                )}

              </Box>

              {/* Register Link */}
              <Divider sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  or
                </Typography>
              </Divider>
              
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Don't have a DPG Finance account yet?
                </Typography>
                <Button 
                  variant="text"
                  onClick={() => navigate('/register')}
                   sx={{ textTransform: 'none', fontWeight: 600, minWidth: 'auto', p: 0.5 }}
                   disabled={isBlocked}
                >
                  Create Account
                </Button>                
              </Box>

            </Paper>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Login;